import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, AlertCircle } from 'lucide-react';
import { authApi } from '../api/client';
import {
    loginStart,
    loginSuccess,
    loginFailure,
    selectAuthStatus,
} from '../store/authSlice';

export default function RegisterPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [localError, setLocalError] = useState(null);

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const status = useSelector(selectAuthStatus);
    const isLoading = status === 'loading';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLocalError(null);

        if (password.length < 8) {
            setLocalError('Password must be at least 8 characters.');
            return;
        }
        if (password !== confirmPassword) {
            setLocalError("Passwords don't match.");
            return;
        }

        dispatch(loginStart());
        try {
            const data = await authApi.register(email, password);
            // Backend returns a token on register, so we go straight in.
            dispatch(loginSuccess(data));
            navigate('/');
        } catch (err) {
            const msg =
                err.response?.status === 409
                    ? 'An account with that email already exists.'
                    : err.response?.data?.message || 'Could not create account. Please try again.';
            setLocalError(msg);
            dispatch(loginFailure(msg));
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <h1 className="font-display text-3xl font-semibold text-slate-900">
                        Commissioner
                    </h1>
                    <p className="text-slate-600 mt-1">Create an account</p>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 space-y-4"
                >
                    {localError && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
                            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                            <span>{localError}</span>
                        </div>
                    )}

                    <div>
                        <label
                            htmlFor="email"
                            className="block text-sm font-medium text-slate-700 mb-1"
                        >
                            Email
                        </label>
                        <input
                            id="email"
                            type="email"
                            required
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            disabled={isLoading}
                        />
                    </div>

                    <div>
                        <label
                            htmlFor="password"
                            className="block text-sm font-medium text-slate-700 mb-1"
                        >
                            Password
                        </label>
                        <input
                            id="password"
                            type="password"
                            required
                            minLength={8}
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            disabled={isLoading}
                        />
                        <p className="text-xs text-slate-500 mt-1">At least 8 characters.</p>
                    </div>

                    <div>
                        <label
                            htmlFor="confirmPassword"
                            className="block text-sm font-medium text-slate-700 mb-1"
                        >
                            Confirm password
                        </label>
                        <input
                            id="confirmPassword"
                            type="password"
                            required
                            autoComplete="new-password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                            disabled={isLoading}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-md disabled:bg-slate-300 disabled:cursor-not-allowed"
                    >
                        <UserPlus size={16} />
                        {isLoading ? 'Creating account...' : 'Create account'}
                    </button>
                </form>

                <p className="text-center text-sm text-slate-600 mt-6">
                    Already have an account?{' '}
                    <Link to="/login" className="text-brand-600 hover:text-brand-700 font-medium">
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}
